import { useCallback, useState } from 'react';
import { useFlags } from '@fullstory/react-hooks';
import { useFetch, AnyRequestLifecycleAction } from '@fullstory/redux-util';
import { OptionType } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { useExperimentConfig } from './useExperimentConfig';
import { ExperimentFeature } from '@fullstory/proto/fs/services/orgs';

export type RequestConfig = {
  choices: string[];
  useAdditionalContext: boolean;
};

const LOADING_GROUP = intl.formatMessage({
  defaultMessage: 'AI is making recommendations...',
  id: 'JjFWmj',
});

const loadingOption = {
  key: 'loading',
  label: '...',
  group: LOADING_GROUP,
  disabled: true,
  value: {
    name: '',
    description: '',
    specSlug: null,
  },
};

export const useAiLabelSuggestions = (
  buildRequest: (reqConfig: RequestConfig, blocklist: string[]) => AnyRequestLifecycleAction,
  getSpecSlug: (name: string) => string | null,
  requestConfigs: RequestConfig[],
) => {
  const flags = useFlags();
  const isAiSuggestionsEnabled = flags.featureFlags().AppFsSpecAi;
  const { experimentOptedOut } = useExperimentConfig();
  const suggestedLabelsOptedOut = experimentOptedOut[ExperimentFeature.FEATURE_SUGGESTED_LABELS];
  const isEnabled = isAiSuggestionsEnabled && !suggestedLabelsOptedOut;
  // as we generate suggestions, we don't want to generate them again
  const [blocklist, setBlocklist] = useState<string[]>([]);
  const fetch = useFetch();

  const [suggestions, setSuggestions] = useState<
    OptionType<{ name: string; description: string; specSlug: string | null }>[]
  >([]);
  const [loading, setLoading] = useState(false);
  const loadSuggestions = useCallback(async () => {
    if (!isEnabled) return;

    setSuggestions([loadingOption]);

    const blocklistAdditions: string[] = [];

    // sequentially fetch suggestions
    for (let i = 0; i < requestConfigs.length; i++) {
      const reqConfig = requestConfigs[i]!;
      const req = buildRequest(reqConfig, [...blocklist, ...blocklistAdditions]);

      try {
        const result = await fetch(req);
        const suggestion = result.suggestion;
        if (!suggestion || suggestion.name === '') {
          continue;
        }
        // add to ongoing blocklist so we don't suggest the same thing again
        blocklistAdditions.push(suggestion.name);

        setSuggestions(suggs => [
          ...suggs.filter(sugg => sugg.key !== 'loading'),
          {
            key: `ai-element-suggestion-${i}`,
            label: suggestion.name,
            group: LOADING_GROUP,
            value: {
              name: suggestion.name,
              description: suggestion.description,
              specSlug: getSpecSlug(suggestion.name),
            },
          },
          loadingOption,
        ]);
      } catch (e) {
        console.error('Error generating label suggestion:', e);
      }
    }

    // set all group names to "Recommended" when done
    setSuggestions(suggs =>
      suggs
        .filter(sugg => sugg.key !== 'loading')
        .map(sugg => ({
          ...sugg,
          group: intl.formatMessage({ defaultMessage: 'Recommended', id: 'VKfWR3' }),
        })),
    );

    setLoading(false);
    setBlocklist(prevBlockList => [...prevBlockList, ...blocklistAdditions]);
  }, [fetch, blocklist, buildRequest, isEnabled, requestConfigs, getSpecSlug]);

  const clearBlockList = useCallback(() => {
    setBlocklist([]);
  }, []);

  return {
    isEnabled,
    suggestions,
    loadSuggestions,
    loading,
    clearBlockList,
  };
};
